import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import getSearchClient from 'server/repository/algolia/client';

import type { IBreadcrumbTitles } from 'components/Breadcrumbs';
import type { ISearchOverlay } from 'components/HeaderNavigation/components/MobileNavigation';
import { IIntroText } from 'components/IntroText';
import TemplateMapper from 'templates/TemplateMapper';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import type { IMetaData } from 'types/MetaData';
import { ProductsPageContainer } from './PageLevelLayouts/Layouts';
import { TrackingService } from 'service/tracking/trackingService';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import { ProductsService } from 'service/products/productsService';

const Breadcrumbs = dynamic(
  () => import('components/Breadcrumbs').then((m: any) => m.Breadcrumbs),
  { ssr: true },
) as any;

const IntroText = dynamic(() => import('components/IntroText').then((m) => m.IntroText), {
  ssr: true,
});

const CategorySubcategory = dynamic(
  () => import('components/CategorySubcategory').then((m: any) => m.CategorySubcategory),
  { ssr: true },
) as any;

const Hero = dynamic(() => import('components/Hero').then((m) => m.Hero), {
  ssr: true,
});

const AnchorNavigation = dynamic(
  () => import('components/AnchorNavigation').then((m) => m.AnchorNavigation),
  { ssr: true },
);

const Spacer = dynamic(() => import('components/Spacer').then((m) => m.Spacer), {
  ssr: true,
});

interface LandingPage {
  productLandingPage: {
    slug: string;
    id: string;
    metaData: IMetaData;
    introText: IIntroText;
    productCategoryPages?: {
      id: string;
      productCategoryName: string;
      slug: string;
    }[];
    anchorNavigationList: {
      id: string;
      anchorNavigations: {
        id: string;
        navigationTitle: string;
        externalComponent: any;
      }[];
    };
  };
  productsText: {
    value: string;
  };
  learnMoreText: {
    value: string;
  };
}

const anchorNavigationTargetStyle = { scrollMarginTop: '150px' };

const DynamicFeaturedContent = dynamic(
  () => import('components/FeaturedContent').then((mod) => mod.FeaturedContent),
  { ssr: true },
);

function ClientProductsPage({
  landingPage,
  breadcrumbsCMSData,
  landingPageComponents,
  dynamicPageComponents,
  searchOverlayData,
  difficultyCopy,
  recipeCopy,
  searchItemLabels,
}: {
  landingPage: LandingPage;
  breadcrumbsCMSData: IBreadcrumbTitles;
  landingPageComponents: any;
  dynamicPageComponents: any;
  searchOverlayData: ISearchOverlay;
  difficultyCopy: Record<string, string>;
  recipeCopy: DictionaryItem[];
  searchItemLabels: Record<string, string>;
}) {
  const { pathTranslations } = useGlobalContext();
  const introData = landingPage?.productLandingPage?.introText;
  const CategorySubcategoryWrapper = ({ category, index }: any) => {
    const productsAmount = category.productSubcategoryPages?.length
      ? category.productSubcategoryPages.reduce(
          (a: number, b: any) => a + ProductsService.getProductsAmount(b),
          0,
        )
      : ProductsService.getProductCategoriesAmount(category);

    const categoryDataDynamic = {
      productsAmount: `${productsAmount} ${landingPage?.productsText?.value}`,
      id: category.id,
      images: {
        desktop: category.keyImage?.desktop,
        mobile: category.keyImage?.mobile,
        mimeType: category.keyImage?.mimeType,
      },
      promoImage: category.promoImage,
      title: category.productCategoryName,
      link: {
        url: `${pathTranslations.productsCategory}${category?.slug || ''}`,
        displayName: landingPage?.learnMoreText?.value,
      },
      button: landingPage?.learnMoreText?.value,
    };

    const subcategoryCardsData = category.productSubcategoryPages
      ?.filter((subCategory: any) => subCategory.subBrand === false)
      // returns card
      .map((subCategory: any) => ({
        productsAmount: `${ProductsService.getProductsAmount(subCategory)} ${
          landingPage?.productsText?.value
        }`,
        id: subCategory?.id,
        image: {
          mobile: subCategory?.promoImage?.asset?.[0]?.mobile,
          desktop: subCategory?.promoImage?.asset?.[0]?.desktop,
          mimeType: subCategory?.promoImage?.asset?.[0]?.mimeType,
        },
        title: subCategory?.subcategoryPageName,
        link: {
          url: `${pathTranslations.productsSubCategory}${category.slug}/${subCategory?.slug}`,
          displayName: landingPage?.learnMoreText?.value,
        },
      }));
    const clickCategoryContainer = () => {
      TrackingService.clickSeeCategoryProduct({
        category: category?.productCategoryName,
      });
    };
    const clickSubcategoryContainer = (card: any) => {
      TrackingService.clickSeeSubcategoryProduct({
        category: category?.productCategoryName,
        subcategory: card?.title,
      });
    };
    return (
      <CategorySubcategory
        categoryData={categoryDataDynamic}
        cardData={subcategoryCardsData}
        categoryCardVariation="object"
        data-test="recipe-category-subcategory"
        trackingEvents={{
          clickCategory: clickCategoryContainer,
          clickSubcategory: clickSubcategoryContainer,
        }}
        isOdd={!!(index % 2 === 0)}
      />
    );
  };
  const [searchOverlayDataExtended, setSearchOverlayDataExtended] = useState(searchOverlayData);

  useEffect(() => {
    if (!searchOverlayDataExtended.searchClient) {
      const algoliaClient = getSearchClient();
      setSearchOverlayDataExtended({
        ...searchOverlayDataExtended,
        searchClient: algoliaClient,
      });
    }
  }, [searchOverlayData]);

  useEffect(() => {
    TrackingService.contentGroups({ cg1: 'product categories' });
  }, []);

  const anchorNavigations =
    landingPage?.productLandingPage?.anchorNavigationList?.anchorNavigations;

  return (
    <>
      <Breadcrumbs titles={breadcrumbsCMSData} pageName={breadcrumbsCMSData?.productsText?.value} />
      {!landingPageComponents?.heroComponent && <Spacer />}
      <ProductsPageContainer>
        {/* 1st position */}
        {landingPageComponents?.heroComponent && (
          <Hero
            imageComponent={landingPageComponents?.heroComponent?.imageComponent}
            title={landingPageComponents?.heroComponent?.title}
            description={landingPageComponents?.heroComponent?.description}
            topRightImageAsset={landingPageComponents?.heroComponent?.topRightImageAsset}
            bottomLeftImageAsset={landingPageComponents?.heroComponent?.bottomLeftImageAsset}
            alignment={landingPageComponents?.heroComponent?.alignment}
            verticalAlignment={landingPageComponents?.heroComponent?.verticalAlignment}
            verticalAlignmentMobile={landingPageComponents?.heroComponent?.verticalAlignmentMobile}
            superText={landingPageComponents?.heroComponent?.superText}
            mobileImage={landingPageComponents?.heroComponent?.mobileImage || {}}
            textColor={landingPageComponents?.heroComponent?.textColor}
          />
        )}
      </ProductsPageContainer>
      {/* 2nd position */}
      {anchorNavigations?.length > 0 && <AnchorNavigation anchorNavigations={anchorNavigations} />}

      {/* 3rd position */}
      <div id="featured-content-carousel" style={anchorNavigationTargetStyle}>
        {landingPageComponents?.featuredContent && (
          <DynamicFeaturedContent
            items={landingPageComponents?.featuredContent?.items}
            dictionary={landingPageComponents?.featuredContent.dictionary}
            superText={landingPageComponents?.featuredContent?.superText || ''}
            title={landingPageComponents?.featuredContent.title}
          />
        )}
      </div>

      {/* 4th position */}
      <div id="category-subcategory" style={anchorNavigationTargetStyle}>
        <ProductsPageContainer>
          {introData && (
            <>
              <IntroText
                super={introData.super || ''}
                title={introData.title || ''}
                description={introData.description || ''}
                titleTag={introData.titleTag ?? ''}
              />
              <Spacer height={20} />
            </>
          )}
          {landingPage?.productLandingPage?.productCategoryPages?.map(
            (category: any, index: any) => (
              <>
                <CategorySubcategoryWrapper category={category} key={category.id} index={index} />
              </>
            ),
          )}
        </ProductsPageContainer>
      </div>

      {/* 5th position */}
      <div id="page-components">
        {dynamicPageComponents && (
          <TemplateMapper
            components={dynamicPageComponents}
            copy={[]}
            recipeCopy={recipeCopy}
            difficultyCopy={difficultyCopy}
            searchItemLabels={searchItemLabels}
            searchData={searchOverlayDataExtended}
          />
        )}
      </div>
    </>
  );
}

export default ClientProductsPage;
